import { useEffect, useState } from 'react';

import {
  AdObj,
  BannerAdObj,
  PlacementId,
  debugLog,
  isAdnamiAd
} from '@schibsted-nmp/advertising-shared';
import { getState } from '@client/state/reducer';
import { getPageOptsWithConsentKeyword } from '@client/xandr/externalId';
import { advtPerformance } from '@schibsted-nmp/advertising-events';

import { publishAdnamiPlacement } from '../utils/dom/handleAdnamiOverlap';
import { Status } from '../types';
import { getTagConfig } from '../xandr/tag';
import {
  debouncedLoadTags,
  defineTag,
  onAdAvailable,
  onAdBadRequest,
  onAdLoaded,
  onAdNoBid,
  onAdNoBidMediated,
  onAdRequested,
  pushToAnqSafe,
  setPageOpts
} from '../xandr/xandr';
import { getConsentOrSubscribe } from '../utils/consent';

export type SetupAdProps = {
  placementId: PlacementId; // The actual placementId xandr uses
  targetId: string; // Used for cycling and actual targeting
  containerId: string; // The container the ad is loaded into from renderAd()
};

// A Creative ID that means the ad is a mediation No Bid
const MEDIATION_NO_BID_CREATIVE = 502388194;

function checkForNoBidCreatives(placementId: PlacementId, creativeId: number) {
  if (creativeId === MEDIATION_NO_BID_CREATIVE) {
    return true;
  }

  const listOfCreativesForPlacement = (window.apntag?.requests?.tags[
    placementId
  ]?.adResponse?.ads || []) as any[];

  if (listOfCreativesForPlacement?.length) {
    return listOfCreativesForPlacement.some(
      (ad) => ad.creative_id === MEDIATION_NO_BID_CREATIVE
    );
  }
  return false;
}
export function useSetupAd({
  placementId,
  targetId,
  containerId
}: SetupAdProps) {
  const [status, setStatus] = useState<Status>('requested');
  const [adObj, setAdObj] = useState<AdObj>();
  const tagConfig = getTagConfig(placementId as PlacementId, targetId);

  const handleAvailable = (_adObj: AdObj) => {
    advtPerformance.markXandrEvents(
      `Creative ID for ${placementId}: ${_adObj.creativeId}`
    );
    if (checkForNoBidCreatives(placementId, _adObj.creativeId)) {
      setStatus('failed');
      debugLog(`No mediation bid for ${targetId}`);
      advtPerformance.markXandrEvents(
        `noMediationBid for ${placementId} with Creative ID: ${_adObj.creativeId}`
      );
      setLocalEntry('No bid');
      return;
    }
    setLocalEntry('Available');
    debugLog({ _adObj });
    setStatus('available');
    setAdObj(_adObj);
    debugLog(`${_adObj.adType} ad available`);
    if (isAdnamiAd(_adObj as BannerAdObj)) {
      publishAdnamiPlacement(containerId);
    }
  };

  const handleAdLoaded = (_adObj: AdObj) => {
    setLocalEntry('Loaded');
    setStatus('loaded');
    setAdObj(_adObj);
    debugLog(`${_adObj.adType} ad loaded`);
  };

  const cleanupEvents = () => {
    if (!window.apntag?.anq) return;

    ['adAvailable', 'adLoaded', 'adNoBid', 'adBadRequest'].forEach(
      (eventType) => {
        window.apntag.anq.push(
          () => window.apntag?.offEvent(eventType, targetId)
        );
      }
    );
  };

  function setLocalEntry(status: string) {
    if (getState().initialState?.env === 'local') {
      const localEntry = document.getElementById(`${placementId}-local-entry`);
      if (localEntry) {
        localEntry.innerHTML += `<p class='m-0 text-12'> - ${status}</p>`;
      }
    }
  }

  useEffect(() => {
    onAdRequested(targetId, () => {
      setLocalEntry('Requested');
    });
    onAdAvailable(targetId, handleAvailable);
    onAdLoaded(targetId, handleAdLoaded);
    onAdBadRequest(targetId, () => {
      setStatus('failed');
      debugLog(`Bad request for ${targetId}`);
      setLocalEntry('Bad request');
    });
    onAdNoBid(targetId, () => {
      setStatus('failed');
      debugLog(`No bid for ${targetId}`);
      setLocalEntry('No bid');
    });
    onAdNoBidMediated(targetId, () => {
      setStatus('failed');
      debugLog(`No bid mediation for ${targetId}`);
      setLocalEntry('No bid mediation');
    });
    getConsentOrSubscribe((permissionValue) => {
      debugLog({ permissionValue });
      const { config } = getState();
      if (config) {
        const pageOpts = getPageOptsWithConsentKeyword(
          config.xandr.pageOpts,
          permissionValue
        );
        setPageOpts(pageOpts, () => debugLog('pageOpts set', pageOpts));
      }
      if (tagConfig) {
        pushToAnqSafe(() => defineTag(tagConfig));
        debouncedLoadTags('relevant-digital');
      }
    });
    return () => {
      setAdObj(undefined);
      cleanupEvents();
    };
  }, [targetId]);

  return { status, adObj };
}
